import { ucfirst } from '~/lib/Utils.js';
import { formatDateTimeLong, formatTime, minutesToDuration, parseTime } from '~/lib/DateTime.js';
import React from 'react';
import { useBookableResourcesQuery, useEventTypesQuery, useFormsQuery } from '~/lib/Queries';
import Questions from '~/lib/Questions';
import { ToolArgumentValue } from '~/components/assistant/ToolArgumentValue.jsx';
import { ClassToObjectTypes, ObjectTypeHumanNames } from '~/lib/ObjectTypes.js';
import { TriggerTypesToHuman } from '~/lib/Flows.js';
import SafeHtml from '~/components/SafeHtml.jsx';
import { Actions } from '~/components/flows/Actions.jsx';


export const ToolArguments = ({ args, tool }) => {
    function humanKey(key) {
        key = key.replace(/_id$/, '');
        key = key.replace(/_ids$/, '');
        key = key.replace(/_/g, ' ').trim();

        if (key === 'bookable resource') {
            return 'Bookable Person/Thing';
        }

        return ucfirst(key);
    }

    const excludeKeys = ['section_id', 'order'];

    const availabilityArgs = ['start', 'end', 'day_of_week'];
    // If args is availabilityArgs, then format as Day of week: Start - End
    if (tool === 'scheduling-bookable-resources-set_availability') {
        return <AvailabilityArguments args={args} />;
    } else if (tool === 'forms-update_question') {
        return <UpdateQuestionArguments args={args} />;
    } else if (tool.includes('update_automation_step')) {
        return <UpdateAutomationStepArguments args={args} />;
    } else if (tool === 'forms-form_details') {
        return <FormDetailsArguments args={args} />;
    } else if (tool === 'forms-update_section') {
        return <UpdateSectionArguments args={args} />;
    } else if (tool === 'reports-create_report') {
        return <CreateReportArguments args={args} />;
    } else if (tool === 'reports-update_report') {
        return <UpdateReportArguments args={args} />;
    } else if (tool === 'scheduling-event-types-update_event_type') {
        return <UpdateEventTypeArguments args={args} />;
    }

    return (
        <div>
            <ul>
                {Object.keys(args || [])
                    .filter(k => !excludeKeys.includes(k))
                    .map(key => (
                        <li key={key}>
                            <ArgumentKeyValue k={humanKey(key)}><ToolArgumentValue k={key} value={args[key]} tool={tool} /></ArgumentKeyValue>
                        </li>
                    ))}
            </ul>
        </div>
    );
};


export const ArgumentKeyValue = ({ k, children }) => {
    let displayChildren = children;
    if (React.isValidElement(children)) {
        displayChildren = children;
    } else if (typeof children === 'object') {
        try {
            displayChildren = JSON.stringify(children);
        } catch (e) {
            displayChildren = Object.keys(children).join(',');
        }
    }

    return (
        <div>
            <span className="font-medium">{k}</span>
            : {displayChildren}
        </div>
    );
};


export const AvailabilityArguments = ({ args }) => {
    return (
        <div>
            <div>
                <ArgumentKeyValue k="Event Types"><ToolArgumentValue k="event_type_ids" value={args.event_type_ids} /></ArgumentKeyValue>
            </div>
            <div>
                <ArgumentKeyValue k="Bookable Person/Thing">
                    <ToolArgumentValue k="bookable_resource_id" value={args.bookable_resource_id} />
                </ArgumentKeyValue>
            </div>
            {args.start_interval > 0 && (
                <div>Start Time Interval: every {minutesToDuration(args.start_interval)}</div>
            )}
            <div>Availability:</div>
            <ul>
            {args.availability?.map((availability, idx) => (
                <li key={idx}>
                    {ucfirst(availability.day_of_week)}: {formatTime(parseTime(availability.start))}{' '}
                    - {formatTime(parseTime(availability.end))}
                </li>
            ))}
            </ul>
        </div>

    );
}


export const UpdateQuestionArguments = ({args}) => {
    const humanType = Questions[args.type]?.name || args.type;

    // Other settings besides required
    let settings = args.settings;
    try {
        settings = JSON.parse(args.settings);
    } catch (e) {
        // ...
    }

    const isRequired = !!settings?.required;
    const otherSettings = settings ? Object.entries(settings).filter(([key]) => key !== 'required') : [];
    console.log('otherSettings=', settings,  otherSettings)

    return (
        <div>

            <div>
                <ArgumentKeyValue k="Label">{args.label}</ArgumentKeyValue>
            </div>
            <div><ArgumentKeyValue k="Type">{humanType}</ArgumentKeyValue></div>
            {isRequired && <div>Required</div>}
            {otherSettings?.length > 0 && (
                <div>
                    <ul>
                        {otherSettings.map(([key, value]) => (
                            <li key={key}>
                                <ArgumentKeyValue k={key}>{Array.isArray(value) ? value.join(', ') : value}</ArgumentKeyValue>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}


export const UpdateAutomationStepArguments = ({args}) => {
    // Extract 'body' from settings
    let settings = args.settings;
    try {
        settings = JSON.parse(args.settings);
    } catch (e) {
        // ...
        console.log('unable to parse settings=', e)
    }

    console.log('settings = ', settings);
    const hasSettings = Object.keys(settings).length > 0;
    const body = settings?.body || '';
    // Remove 'body' from settings
    delete settings.body;

    return (
        <div>
            <div>
                <ArgumentKeyValue k="Type">{ObjectTypeHumanNames[args.flowable_type] || ObjectTypeHumanNames[ClassToObjectTypes[args.flowable_type]]}</ArgumentKeyValue>
            </div>
            <div>
                <ArgumentKeyValue k="Triggered">{minutesToDuration(args.offset)} {TriggerTypesToHuman[args.trigger]}</ArgumentKeyValue>
            </div>
            <div>
                <ArgumentKeyValue k="Action">{(Actions[args.action]?.verb||'Send') + ' ' + Actions[args.action]?.name || args.action}</ArgumentKeyValue>
            </div>
            <div className={hasSettings ? 'border-t pt-3 mt-3' : ''}>
                {Object.keys(settings).map((key) => (
                    <div key={key}>
                        <ArgumentKeyValue k={ucfirst(key)}>{settings[key]}</ArgumentKeyValue>
                    </div>
                ))}
                {!!body && (
                    <div>
                        <ArgumentKeyValue k="Body">
                            <div className="p-3 bg-white rounded-lg">
                                <SafeHtml html={body} iframe />
                            </div>
                        </ArgumentKeyValue>
                    </div>
                )}
            </div>
        </div>
    );
}


export const FormDetailsArguments = ({args}) => {
    return (
        <div>
            <div className="text-gray-500 text-sm">
                Getting details for form {args.form_id}
            </div>
        </div>
    );
}


export const UpdateSectionArguments = ({args}) => {
    const { data: formsData } = useFormsQuery();
    const formName = formsData?.find(f => f.id === args.form_id)?.name || `Form #${args.form_id}`;

    let conditional = args.conditional;
    if (typeof conditional === 'string') {
        try {
            conditional = JSON.parse(conditional);
        } catch (e) {
            // keep as string
        }
    }

    function formatGoto(goto) {
        if (!goto) return '';
        if (goto === 'submit') return 'Submit the form';
        if (goto === 'condition') return 'Based on answer';
        if (typeof goto === 'string' && goto.startsWith('jump-')) {
            return `Go to section #${goto.replace('jump-', '')}`;
        }
        return goto;
    }

    return (
        <div>
            <div>
                <ArgumentKeyValue k="Form">{formName}</ArgumentKeyValue>
            </div>
            {args.order !== undefined && (
                <div>
                    <ArgumentKeyValue k="Order">{args.order}</ArgumentKeyValue>
                </div>
            )}
            {conditional && typeof conditional === 'object' && (
                <div>
                    <div className="font-medium mt-1">Conditional Logic</div>
                    {conditional.goto && conditional.goto !== 'condition' && (
                        <div className="ml-2 text-sm">{formatGoto(conditional.goto)}</div>
                    )}
                    {conditional.goto === 'condition' && conditional.question && (
                        <div className="ml-2 text-sm">
                            <div>Based on answer to question #{conditional.question}:</div>
                            {conditional.answerGoto && (
                                <ul className="ml-2">
                                    {Object.entries(conditional.answerGoto).map(([answer, goto]) => (
                                        <li key={answer}>
                                            "{answer}" → {formatGoto(goto)}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}



const reportTypeLabels = {
    Clients: 'Clients',
    Events: 'Events',
    Forms: 'Forms',
    PackageGrants: 'Package Grants',
    Classes: 'Classes',
    Payments: 'Payments',
    GiftCertificates: 'Gift Certificates',
    Invoices: 'Invoices',
};

const parseReportSettings = (settingsStr) => {
    let settings;
    try {
        settings = typeof settingsStr === 'string' ? JSON.parse(settingsStr) : settingsStr;
    } catch (e) {
        return null;
    }
    if (!settings || typeof settings !== 'object') return null;

    const summary = [];

    if (settings.filterModel && Object.keys(settings.filterModel).length > 0) {
        summary.push('Filtered by: ' + Object.keys(settings.filterModel).join(', '));
    }

    const columnState = settings.columnState;
    if (Array.isArray(columnState)) {
        const sorted = columnState.filter(c => c.sort);
        const grouped = columnState.filter(c => c.rowGroup);
        const aggregated = columnState.filter(c => c.aggFunc);

        if (sorted.length > 0) {
            summary.push('Sorted by: ' + sorted.map(c => `${c.colId} (${c.sort})`).join(', '));
        }
        if (grouped.length > 0) {
            summary.push('Grouped by: ' + grouped.map(c => c.colId).join(', '));
        }
        if (aggregated.length > 0) {
            summary.push('Aggregation: ' + aggregated.map(c => `${c.aggFunc}(${c.colId})`).join(', '));
        }
    }

    return summary.length > 0 ? summary : null;
};

export const CreateReportArguments = ({ args }) => {
    const settingsSummary = parseReportSettings(args.settings);
    return (
        <div>
            <div>
                <ArgumentKeyValue k="Name">{args.name}</ArgumentKeyValue>
            </div>
            <div>
                <ArgumentKeyValue k="Type">{reportTypeLabels[args.type] || args.type}</ArgumentKeyValue>
            </div>
            {settingsSummary?.map((line, idx) => (
                <div key={idx} className="text-sm text-gray-500">{line}</div>
            ))}
        </div>
    );
};

export const UpdateEventTypeArguments = ({ args }) => {
    const paymentLabels = {
        full: 'Pay in full',
        'full-or-skip': 'Pay in full or skip',
        deposit: 'Deposit',
        none: 'No payment required',
        vault: 'Require card on file',
        'vault-or-full': 'Card on file or pay in full',
    };

    const locationLabels = {
        google: 'Google Meet',
        zoom: 'Zoom',
        address: 'Address',
        custom: 'Custom link',
    };

    const isNew = !args.id;
    const typeLabel = args.type === 'series' ? 'Series' : args.type === 'class' ? 'Class' : 'Event';

    return (
        <div>
            <div>
                <ArgumentKeyValue k="Name">{args.name}</ArgumentKeyValue>
            </div>
            <div>
                <ArgumentKeyValue k="Type">{typeLabel}</ArgumentKeyValue>
            </div>
            {/*{args.description && (*/}
            {/*    <div className="text-sm text-gray-500 mt-1">{args.description}</div>*/}
            {/*)}*/}
            {args.duration && (
                <div>
                    <ArgumentKeyValue k="Duration">{minutesToDuration(args.duration)}</ArgumentKeyValue>
                </div>
            )}
            {(args.padding_start > 0 || args.padding_end > 0) && (
                <div className="text-sm text-gray-500">
                    Buffer: {args.padding_start > 0 ? `${minutesToDuration(args.padding_start)} before` : ''}
                    {args.padding_start > 0 && args.padding_end > 0 ? ', ' : ''}
                    {args.padding_end > 0 ? `${minutesToDuration(args.padding_end)} after` : ''}
                </div>
            )}
            {args.price !== undefined && parseFloat(args.price) > 0 && (
                <div>
                    <ArgumentKeyValue k="Price">{parseFloat(args.price) === 0 ? 'Free' : `$${args.price}`}</ArgumentKeyValue>
                </div>
            )}
            {args.payment_requirement && (
                <div>
                    <ArgumentKeyValue k="Payment">{paymentLabels[args.payment_requirement] || args.payment_requirement}</ArgumentKeyValue>
                </div>
            )}
            {args.payment_requirement === 'deposit' && args.deposit_amount && (
                <div className="text-sm text-gray-500 ml-2">Deposit: ${args.deposit_amount}</div>
            )}
            {args['class-max-attendees'] && args['class-max-attendees'] > 1 && (
                <div>
                    <ArgumentKeyValue k="Max Attendees">{args['class-max-attendees']}</ArgumentKeyValue>
                </div>
            )}
            {args['location-type'] && (
                <div>
                    <ArgumentKeyValue k="Location">
                        <span>
                            {locationLabels[args['location-type']] || args['location-type']}
                            {args['location-description'] ? `: ${args['location-description']}` : ''}
                        </span>
                    </ArgumentKeyValue>
                </div>
            )}
            {args.bookable_resources && (
                <div>
                    <ArgumentKeyValue k="Bookable People/Things">
                        <ToolArgumentValue k="bookable_resource_ids" value={args.bookable_resources} />
                    </ArgumentKeyValue>
                </div>
            )}
            {args.late_join && (
                <div className="text-sm text-gray-500">Allows late enrollment</div>
            )}
        </div>
    );
};

export const UpdateReportArguments = ({ args }) => {
    const settingsSummary = parseReportSettings(args.settings);
    return (
        <div>
            {args.name && (
                <div>
                    <ArgumentKeyValue k="Name">{args.name}</ArgumentKeyValue>
                </div>
            )}
            {args.type && (
                <div>
                    <ArgumentKeyValue k="Type">{reportTypeLabels[args.type] || args.type}</ArgumentKeyValue>
                </div>
            )}
            {settingsSummary?.map((line, idx) => (
                <div key={idx} className="text-sm text-gray-500">{line}</div>
            ))}
        </div>
    );
};
